import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, BarChart3, PlusCircle, User, HelpCircle, LogOut, ScrollText, Book } from 'lucide-react';
// Add proper TypeScript interfaces
interface SidebarProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}
interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  description?: string;
  onClick?: () => void;
}
export function Sidebar({
  isMobileMenuOpen,
  setIsMobileMenuOpen
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Function to handle logout
  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    navigate('/login');
  };
  return <>
      {/* Overlay for mobile - only visible when menu is open */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-212" />}
      {/* Sidebar - hidden on mobile/tablet unless menu is open */}
      <div className={`${isMobileMenuOpen ? 'fixed left-0 top-0 bottom-0 w-[240px] z-40' : 'hidden'} lg:relative lg:block lg:w-[240px] bg-[#376770] text-white flex flex-col h-screen lg:h-full border-r border-[#7F9EA4]/30 overflow-hidden`} data-id="element-213">
        <div className="p-4 sm:p-6 flex flex-col items-start justify-start border-b border-[#7F9EA4]/30" data-id="element-214">
          <Link to="/dashboard" className="flex items-center" data-id="element-215">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jJkprGARCzfGvbLTaJLb3b/Elze_logo.svg" alt="Elze Logo" className="h-10 w-auto filter brightness-0 invert-[1]" data-id="element-216" />
          </Link>
        </div>
        {/* Flex container for main menu and bottom menu */}
        <div className="flex flex-col flex-grow overflow-hidden" data-id="element-217">
          {/* Main menu with scrolling */}
          <div className="flex-1 overflow-y-auto" data-id="element-218">
            <nav className="py-4 sm:py-6" data-id="element-219">
              <div className="space-y-1 px-3" data-id="element-220">
                <NavItem to="/dashboard" icon={<LayoutDashboard size={18} data-id="element-222" />} label="Dashboard" description="Overview of works, campaigns, interactions, and profile performance." active={location.pathname === '/' || location.pathname === '/dashboard'} onClick={() => setIsMobileMenuOpen(false)} data-id="element-221" />
                <NavItem to="/my-works" icon={<Book size={18} data-id="element-224" />} label="My Works" description="List of uploaded works from various channels (on Elze, from Kindle, etc.)" active={location.pathname === '/my-works'} onClick={() => setIsMobileMenuOpen(false)} data-id="element-223" />
                <NavItem to="/royalty-management" icon={<BarChart3 size={18} data-id="element-226" />} label="Royalties" description="Management and distribution of royalties from your works' copyrights. Monitor revenue and payments." active={location.pathname === '/royalty-management'} onClick={() => setIsMobileMenuOpen(false)} data-id="element-225" />
                <NavItem to="/contract-management" icon={<ScrollText size={18} data-id="element-228" />} label="Contracts" description="View and manage transferred or available rights. Generate contracts with guided builder." active={location.pathname === '/contract-management'} onClick={() => setIsMobileMenuOpen(false)} data-id="element-227" />
              </div>
            </nav>
          </div>
          {/* Shortcuts menu (always visible) */}
          <div className="px-3 py-4" data-id="element-229">
            <div className="px-1 text-xs font-semibold text-gray-200 uppercase tracking-wider mb-2" data-id="element-230">
              SHORTCUTS
            </div>
            <div className="space-y-1" data-id="element-231">
              <NavItem to="#" icon={<PlusCircle size={18} data-id="element-233" />} label="New Work" onClick={() => setIsMobileMenuOpen(false)} data-id="element-232" />
              <NavItem to="/profile" icon={<User size={18} data-id="element-235" />} label="Public Profile" active={location.pathname === '/profile'} onClick={() => setIsMobileMenuOpen(false)} data-id="element-234" />
            </div>
          </div>
          {/* Bottom menu (completely sticky to the bottom) */}
          <div className="px-3 py-4 border-t border-[#7F9EA4]/30 bg-[#2d5459]/80 mt-auto" data-id="element-236">
            <div className="space-y-1" data-id="element-237">
              <NavItem to="#" icon={<HelpCircle size={18} data-id="element-239" />} label="Support" onClick={() => setIsMobileMenuOpen(false)} data-id="element-238" />
              <NavItem to="/login" icon={<LogOut size={18} data-id="element-241" />} label="Logout" onClick={handleLogout} data-id="element-240" />
            </div>
          </div>
        </div>
      </div>
    </>;
}
function NavItem({
  to,
  icon,
  label,
  active = false,
  description = '',
  onClick
}: NavItemProps) {
  return <Link to={to} className={`flex items-center px-3 py-2.5 text-[15px] font-medium rounded-md transition-colors ${active ? 'bg-[#C6D6AB] text-gray-800' : 'text-gray-100 hover:bg-[#2d5459]/70 hover:text-white'}`} title={description} onClick={onClick} data-id="element-242">
      <span className="mr-3 opacity-80" data-id="element-243">{icon}</span>
      {label}
    </Link>;
}